import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "../Reveal";
import { CHAPTERS } from "../../lib/brand";

const Chapter = ({ ch, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [28, -28]);
  const reversed = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`chapter-${ch.no}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
    >
      {/* Text */}
      <div className={`lg:col-span-6 ${reversed ? "lg:order-2 lg:pl-10" : "lg:pr-10"}`}>
        <Reveal>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-6xl md:text-7xl text-teal-400/25 leading-none">
              {ch.no}
            </span>
            <span className="font-mono text-[11px] tracking-[0.35em] text-teal-300 pb-2">
              {ch.label}
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="mt-4 font-serif font-light tracking-tight text-4xl md:text-6xl text-zinc-100">
            {ch.title}
          </h3>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-md text-zinc-400 font-light leading-relaxed text-base md:text-lg">
            {ch.body}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 flex items-center gap-3">
            <span className="h-px w-8 bg-teal-400/50" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              {ch.tag}
            </span>
          </div>
        </Reveal>
      </div>

      {/* Image */}
      <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
        <Reveal y={60}>
          <div className="relative overflow-hidden rounded-sm border border-white/5 bg-gradient-to-b from-zinc-900/50 to-black/70">
            <motion.img
              style={{ y }}
              src={ch.image}
              alt={ch.title}
              className="h-[420px] md:h-[560px] w-full object-contain"
            />
            {!ch.light && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
            )}
            <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.3em] text-white/70 bg-black/40 backdrop-blur px-3 py-1.5">
              FIG. {ch.no}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export const Manifesto = () => (
  <section
    id="manifesto"
    data-testid="manifesto-section"
    className="relative bg-[#030303] py-28 md:py-40"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal className="mb-20 md:mb-28 max-w-3xl">
        <span className="font-mono text-[11px] tracking-[0.4em] text-teal-300">
          THE PHILOSOPHY
        </span>
        <h2 className="mt-6 font-serif font-light tracking-tighter text-4xl md:text-6xl lg:text-7xl text-zinc-100 leading-[1]">
          Whatever surrounds water,{" "}
          <span className="italic text-teal-300">it keeps.</span>
        </h2>
        <p className="mt-6 text-zinc-400 font-light text-lg max-w-xl">
          Three truths — ancient and measured — behind every bottle we fill.
        </p>
      </Reveal>

      <div className="space-y-28 md:space-y-40">
        {CHAPTERS.map((ch, i) => (
          <Chapter key={ch.no} ch={ch} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Manifesto;
